import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  AuthenticationProvider,
  ApiAuthenticationProviderTypes,
  AuthenticationService,
  PasswordFormSignInComponent,
  SelectSignInProviderComponent,
} from '@aodocs/authentication';
import { MessageBannerModule } from '@aodocs/message-banner';
import { TranslocoUtils } from '@aodocs/shared-translation';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { filter, first, Observable, Subject, takeUntil } from 'rxjs';

import { LOGIN_PAGE_CONFIG, LoginPageConfig } from './config/login-page-config';
import * as errorLabels from './const/errors';

@Component({
  selector: 'aodocs-login-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MessageBannerModule,
    TranslocoModule,
    MatProgressSpinnerModule,
    MatCardModule,
    PasswordFormSignInComponent,
    SelectSignInProviderComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'loginPage',
      loader: TranslocoUtils.loader((lang) => import(`../assets/i18n/lib/login-page/login-page-${lang}.json`)),
    }),
  ],
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
})
export class LoginPageComponent implements OnInit, OnDestroy {
  isErrorBannerVisible = false;
  isEmailBannerVisible = false;

  error: string | undefined;
  emailMessage?: string;
  loading = false;
  loadingProviders = true;
  isPendingAuth: Observable<boolean> | undefined;
  showEmailForm = false;
  providers: ApiAuthenticationProviderTypes[] = [];

  private readonly loginHint: string | undefined;
  private domain: string | undefined;
  private isDestroyed$ = new Subject<void>();

  constructor(
    @Inject(LOGIN_PAGE_CONFIG) public readonly configuration: LoginPageConfig,
    private readonly authenticationService: AuthenticationService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly transloco: TranslocoService
  ) {
    this.isPendingAuth = this.authenticationService.pendingRedirect$;
    this.loginHint = this.router.routerState.snapshot.root.queryParams['authorizationEmail'];
    this.domain = (this.router.routerState.snapshot.root.queryParams['aodocs-domain'] ??
      this.router.routerState.snapshot.root.queryParams['domain']) as string | undefined;

    this.authenticationService.user$
      .pipe(
        filter((user) => !!user),
        first()
      )
      .subscribe(() => {
        this.loading = true;
        if (configuration.redirectUrl) {
          this.router.navigate(configuration.redirectUrl as string[], { queryParamsHandling: 'preserve' });
        } else if (configuration.redirectFunction) {
          const { commands, extra } = configuration.redirectFunction(this.router);
          this.router.navigate(commands, extra);
        }
      });

    this.authenticationService.error$
      .pipe(
        takeUntil(this.isDestroyed$),
        filter((error) => !!error)
      )
      .subscribe(() => {
        this.loginError();
      });
  }

  ngOnInit(): void {
    this.loading = false;
    this.authenticationService
      .getAuthProvider(this.domain)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (providers) => {
          this.providers = providers;
          this.loadingProviders = false;
        },
        error: () => {
          this.providers = [];
          this.loadingProviders = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  initEmailSentWarning(email: string): void {
    this.emailMessage = this.transloco.translate('loginPage.CONFIRMATION_EMAIL_SENT', { email });
    this.isEmailBannerVisible = true;
  }

  startAuthentication(provider: AuthenticationProvider): void {
    this.isErrorBannerVisible = false;
    this.isEmailBannerVisible = false;
    if (provider === AuthenticationProvider.PASSWORD) {
      this.showEmailForm = true;
    } else {
      this.loading = true;
      this.authenticationService.authenticate(
        provider,
        this.loginHint,
        this.configuration.additionalScopesFactory ? this.configuration.additionalScopesFactory(this.activatedRoute) : undefined
      );
    }
  }

  private loginError(): void {
    this.error = `Error: ${errorLabels.loginError}`;
    this.isErrorBannerVisible = true;
    this.loading = false;
  }
}
